
import { Coordinates, ChargingStation, MapBounds } from "../types";

// URL til din Firebase Cloud Function proxy
const NOBIL_PROXY_URL = "https://us-central1-fricamp-2810e.cloudfunctions.net/nobilProxy";

export let lastNobilDebug = {
  lastUrl: "",
  lastResponse: null as any,
  lastError: null as any,
  timestamp: 0
};

let lastRequestTime = 0;
const MIN_REQUEST_GAP = 1000; // 1s throttle for stability

// Queue mechanism to serialize requests and enforce delay
let pendingPromise: Promise<void> = Promise.resolve();

const throttle = async () => {
  // Capture the current tail of the queue
  const previousTask = pendingPromise;
  
  // Create a new task that waits for the previous one, then waits for the time gap
  const currentTask = (async () => {
    try {
      await previousTask;
    } catch (e) {
      // Ignore errors in previous tasks so the queue doesn't stall
    }

    const now = Date.now();
    const timeSinceLast = now - lastRequestTime;
    if (timeSinceLast < MIN_REQUEST_GAP) {
      await new Promise(resolve => setTimeout(resolve, MIN_REQUEST_GAP - timeSinceLast));
    }
    lastRequestTime = Date.now();
  })();

  // Update the global queue tail
  pendingPromise = currentTask;
  
  // Wait for our turn
  await currentTask;
};

/**
 * Henter ladestasjoner innenfor et gitt rektangel (kartutsnitt).
 */
export const fetchChargersInBounds = async (bounds: MapBounds): Promise<ChargingStation[]> => {
  if (!bounds || typeof bounds.latMax !== 'number') return [];
  
  await throttle();

  // Oppdatert format: NOBIL dokumentasjon bruker ofte mellomrom: (lat, lon)
  const ne = `(${bounds.latMax.toFixed(6)}, ${bounds.lonMax.toFixed(6)})`;
  const sw = `(${bounds.latMin.toFixed(6)}, ${bounds.lonMin.toFixed(6)})`;

  const params = new URLSearchParams({
    action: "search",
    type: "rectangle",
    northeast: ne,
    southwest: sw,
    apiversion: "3"
  });

  const url = `${NOBIL_PROXY_URL}?${params.toString()}`;
  
  lastNobilDebug.lastUrl = url;
  lastNobilDebug.timestamp = Date.now();
  lastNobilDebug.lastResponse = "Laster...";
  lastNobilDebug.lastError = null;

  try {
    const response = await fetch(url);
    
    if (!response.ok) {
      const err = `NOBIL Proxy Error: ${response.status} ${response.statusText}`;
      lastNobilDebug.lastError = err;
      return [];
    }
    
    const data = await response.json();
    
    // Lagre HELE objektet for debugging, slik at vi ser evt. feilmeldinger
    lastNobilDebug.lastResponse = data;

    if (data.error) {
       lastNobilDebug.lastError = `NOBIL Error: ${data.error}`;
       return [];
    }

    return parseNobilResponse(data);
  } catch (error: any) {
    lastNobilDebug.lastError = error.message || "Network error";
    console.error("NOBIL fetch failed:", error);
    return [];
  }
};

/**
 * Henter den nærmeste ladestasjonen til et gitt punkt.
 */
export const getNearestCharger = async (coords: Coordinates, maxDistanceMeters: number = 5000): Promise<ChargingStation | null> => {
  if (!coords) return null;
  
  await throttle();

  const params = new URLSearchParams({
    action: "search",
    type: "near",
    lat: coords.latitude.toFixed(6),
    long: coords.longitude.toFixed(6),
    distance: maxDistanceMeters.toString(),
    limit: "1",
    apiversion: "3"
  });

  const url = `${NOBIL_PROXY_URL}?${params.toString()}`;
  lastNobilDebug.lastUrl = url;
  lastNobilDebug.timestamp = Date.now();

  try {
    const response = await fetch(url);
    if (!response.ok) return null;
    
    const data = await response.json();
    lastNobilDebug.lastResponse = data;
    const stations = parseNobilResponse(data);
    return stations.length > 0 ? stations[0] : null;
  } catch (error: any) {
    lastNobilDebug.lastError = error.message;
    console.warn("NOBIL proximity search failed:", error);
    return null;
  }
};

const parseNobilResponse = (data: any): ChargingStation[] => {
  if (!data) return [];
  
  // NOBIL API v3 returnerer data i objektet "chargerstations"
  const stationsData = data.chargerstations || data.results || [];
  if (!Array.isArray(stationsData)) return [];
  
  return stationsData.map((item: any): ChargingStation | null => {
    // Dataen ligger i 'csmd' (Charge Station Metadata)
    const s = item.csmd;
    if (!s) return null;

    // Koordinat-parsing fra Position-feltet: "(60.9845397,9.2453331)"
    let lat = 0, lon = 0;
    if (s.Position) {
      const coords = s.Position.replace(/[() ]/g, "").split(",");
      lat = parseFloat(coords[0]);
      lon = parseFloat(coords[1]);
    } else {
      // Fallback hvis de bruker flate felt (skjer i noen versjoner)
      const latStr = String(s.wgs84_lat || "0").replace(/[()]/g, "");
      const lonStr = String(s.wgs84_long || "0").replace(/[()]/g, "");
      lat = parseFloat(latStr);
      lon = parseFloat(lonStr);
    }
    
    // Samle kontakttyper fra 'attr' (Attributes)
    const connectors = new Set<string>();
    if (item.attr && item.attr.conn) {
      Object.values(item.attr.conn).forEach((c: any) => {
        const type = c["4"]?.trans || c["4"]?.attrval; 
        if (type) connectors.add(type);
      });
    }

    // Bygg adresse
    const street = s.Street || "";
    const house = s.House_number || "";
    const address = street ? `${street} ${house}`.trim() : "Ukjent adresse";

    return {
      id: String(s.id),
      name: s.name || "Ladestasjon",
      operator: s.Operator || s.Owned_by,
      location: { latitude: lat, longitude: lon },
      chargingPoints: parseInt(s.Number_charging_points) || 1,
      connectors: Array.from(connectors),
      address,
      city: s.City,
      contactInfo: s.Contact_info,
      distance: item.distance 
    };
  }).filter((s): s is ChargingStation => s !== null && !isNaN(s.location.latitude) && s.location.latitude !== 0);
};
